import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import grapesjs from 'grapesjs';
import 'grapesjs/dist/css/grapes.min.css';
import AdminSectionsList from './AdminSectionsList';

interface Page {
  id: number;
  titleAr: string;
  parent: string;
  template: string;
  order: number;
  status: 'published' | 'draft';
  updatedAt: string;
  content: string;
}

const fakePages: Page[] = [
  { id: 1, titleAr: 'من نحن', parent: '', template: 'الافتراضي', order: 0, status: 'published', updatedAt: '2025/02/10 10:23 ص', content: '<h1>عن الشركة</h1>' },
  { id: 2, titleAr: 'الخدمات', parent: '', template: 'الافتراضي', order: 0, status: 'published', updatedAt: '2025/02/10 10:24 ص', content: '<h1>خدماتنا</h1>' },
];

const AdminPageForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [page, setPage] = useState<Page | null>(null);
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [activeTab, setActiveTab] = useState<'visual' | 'code'>('visual');
  const [code, setCode] = useState<{ html: string; css: string }>({ html: '', css: '' });
  const [blocks, setBlocks] = useState<any[]>([]);
  const editorRef = useRef<any>(null);
  const editorContainerRef = useRef<HTMLDivElement>(null);

  // بيانات السكاشن الجاهزة (يمكن استيرادها من AdminSectionsList أو تعريفها هنا مؤقتًا)
  const librarySections = [
    {
      id: 1,
      name: 'سكشن البطل (Hero)',
      description: 'هيدر متحرك مع صور وشعار الشركة ورسالة ترحيبية.',
      thumbnail: '/images/image1.jpg',
    },
    {
      id: 2,
      name: 'خدماتنا',
      description: 'عرض الخدمات الرئيسية للشركة مع أيقونات ووصف مختصر.',
      thumbnail: '/images/image2.jpg',
    },
    {
      id: 3,
      name: 'لماذا نحن',
      description: 'مميزات الشركة وأسباب اختيارنا مع أيقونات وإحصائيات.',
      thumbnail: '/images/image3.jpg',
    },
    {
      id: 4,
      name: 'الرؤية والرسالة والأهداف',
      description: 'عرض رؤية ورسالة وأهداف الشركة في بطاقات أنيقة.',
      thumbnail: '/images/image4.jpg',
    },
  ];

  const [showLibrary, setShowLibrary] = useState(false);

  useEffect(() => {
    if (isEdit) {
      const found = fakePages.find(p => p.id === Number(id));
      setPage(found || null);
    } else {
      setPage({ id: Date.now(), titleAr: '', parent: '', template: 'الافتراضي', order: 0, status: 'draft', updatedAt: new Date().toISOString(), content: '' });
    }
    setLoading(false);
  }, [id, isEdit]);

  // تهيئة GrapesJS
  useEffect(() => {
    if (editorContainerRef.current && page && !editorRef.current) {
      editorRef.current = grapesjs.init({
        container: editorContainerRef.current,
        fromElement: false,
        height: '60vh',
        width: '100%',
        storageManager: { autoload: false },
        plugins: ['gjs-preset-webpage'],
        pluginsOpts: {
          'gjs-preset-webpage': {
            blocksBasicOpts: { flexGrid: true },
            navbarOpts: false,
            countdownOpts: false,
            formsOpts: false,
            exportOpts: false,
            aviaryOpts: false,
            filestackOpts: false,
          },
        },
        blockManager: {
          appendTo: undefined,
        },
        canvas: {
          styles: [
            'https://fonts.googleapis.com/css?family=Cairo:400,700&display=swap',
            'body { font-family: Cairo, sans-serif; direction: rtl; }',
          ],
        },
        i18n: {
          locale: 'ar',
          messages: { ar: { 'style-manager': 'إدارة النمط', 'trait-manager': 'خصائص العنصر' } },
        },
      });
      // دعم RTL
      const body = editorRef.current.Canvas.getBody();
      if (body) {
        body.setAttribute('dir', 'rtl');
      }
      if (page.content) {
        editorRef.current.setComponents(page.content);
      }
      setTimeout(() => {
        const allBlocks = editorRef.current.BlockManager.getAll().map((block: any) => ({
          id: block.id,
          label: block.get('label'),
          media: block.get('media'),
        }));
        setBlocks(allBlocks);
      }, 500);
    }
    return () => {
      if (editorRef.current) {
        editorRef.current.destroy();
        editorRef.current = null;
      }
    };
  }, [page]);

  useEffect(() => {
    if (activeTab === 'code' && editorRef.current) {
      setCode({
        html: editorRef.current.getHtml(),
        css: editorRef.current.getCss(),
      });
    }
  }, [activeTab]);

  const handleCopy = () => {
    navigator.clipboard.writeText(`<style>\n${code.css}\n</style>\n${code.html}`);
  };

  const handleAddBlock = (blockId: string) => {
    if (editorRef.current) {
      editorRef.current.BlockManager.get(blockId)?.trigger('select');
      editorRef.current.runCommand('core:component-add', { block: blockId });
    }
  };

  // دالة إدراج سكشن من المكتبة
  const handleInsertSection = (sectionId: number) => {
    // هنا يمكنك ربط كل سكشن بمحتوى HTML/JSON حقيقي لاحقًا
    if (editorRef.current) {
      // مثال: إدراج ديف placeholder باسم السكشن
      const section = librarySections.find(s => s.id === sectionId);
      if (section) {
        editorRef.current.addComponents(`<section class='section-library-block'><h2>${section.name}</h2><p>${section.description}</p></section>`);
      }
    }
    setShowLibrary(false);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    if (!page) return;
    setPage({ ...page, [e.target.name]: e.target.value });
  };

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    if (!page) return;
    const html = editorRef.current ? editorRef.current.getHtml() : '';
    setSuccess(true);
    setTimeout(() => {
      setSuccess(false);
      navigate('/admin/pages');
    }, 1000);
  };

  if (loading || !page) return <div className="p-8">جاري التحميل...</div>;

  return (
    <div className="flex min-h-screen bg-gray-100">
      {/* Sidebar */}
      <aside className="w-full max-w-xs bg-white shadow-lg p-6 flex flex-col gap-6 border-l rtl:border-l-0 rtl:border-r">
        {/* نشر */}
        <div className="mb-6 p-4 border rounded-lg bg-gray-50">
          <div className="flex items-center justify-between mb-2">
            <span className="font-bold text-blue-900">نشر</span>
            <button className="text-xs text-blue-700 underline" onClick={() => window.open('/', '_blank')}>معاينة</button>
          </div>
          <div className="mb-2">
            <span className="text-gray-700">الحالة:</span>
            <span className="ml-2 font-bold text-gray-900">{page.status === 'draft' ? 'مسودة' : 'منشورة'}</span>
          </div>
          <button onClick={handleSave} className="w-full bg-blue-700 text-white py-2 rounded-lg font-bold hover:bg-blue-800 transition mb-2">{page.status === 'draft' ? 'حفظ المسودة' : 'تحديث'}</button>
          <button onClick={() => setPage({ ...page, status: page.status === 'draft' ? 'published' : 'draft' })} className="w-full bg-gray-200 text-gray-700 py-2 rounded-lg font-bold hover:bg-gray-300 transition">تبديل الحالة</button>
        </div>
        {/* خصائص الصفحة */}
        <div className="mb-6 p-4 border rounded-lg bg-gray-50">
          <span className="font-bold text-blue-900 block mb-2">خصائص الصفحة</span>
          <div className="mb-2">
            <label className="block text-gray-700 mb-1">Parent</label>
            <select name="parent" value={page.parent} onChange={handleChange} className="w-full px-3 py-2 border rounded-lg">
              <option value="">(بدون أب)</option>
              <option value="1">من نحن</option>
              <option value="2">الخدمات</option>
            </select>
          </div>
          <div className="mb-2">
            <label className="block text-gray-700 mb-1">نموذج</label>
            <select name="template" value={page.template} onChange={handleChange} className="w-full px-3 py-2 border rounded-lg">
              <option value="الافتراضي">القالب الافتراضي</option>
              <option value="landing">صفحة هبوط</option>
            </select>
          </div>
          <div>
            <label className="block text-gray-700 mb-1">ترتيب</label>
            <input type="number" name="order" value={page.order} onChange={handleChange} className="w-full px-3 py-2 border rounded-lg" />
          </div>
        </div>
      </aside>
      {/* Main Content */}
      <main className="flex-1 p-8 flex flex-col gap-6">
        {/* زر إدراج سكشن من المكتبة */}
        <div className="flex justify-end mb-4">
          <button
            type="button"
            onClick={() => setShowLibrary(true)}
            className="bg-green-600 text-white font-bold px-6 py-2 rounded-lg shadow hover:bg-green-700 transition"
          >
            إدراج سكشن من المكتبة
          </button>
        </div>
        {/* نافذة مكتبة السكاشن */}
        {showLibrary && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center">
            <div className="bg-white rounded-xl shadow-lg p-8 max-w-2xl w-full relative">
              <button onClick={() => setShowLibrary(false)} className="absolute top-4 left-4 text-gray-500 hover:text-red-600 font-bold text-xl">×</button>
              <h2 className="text-xl font-bold text-blue-900 mb-6 text-center">مكتبة السكاشن الجاهزة</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {librarySections.map(section => (
                  <div key={section.id} className="border rounded-lg p-4 flex flex-col items-center gap-2 hover:shadow-lg transition cursor-pointer bg-gray-50">
                    <img src={section.thumbnail} alt={section.name} className="w-32 h-20 object-cover rounded shadow mb-2" />
                    <div className="font-bold text-blue-900 mb-1">{section.name}</div>
                    <div className="text-gray-600 text-sm mb-2 text-center">{section.description}</div>
                    <button
                      onClick={() => handleInsertSection(section.id)}
                      className="bg-blue-700 text-white px-4 py-1 rounded hover:bg-blue-800 transition"
                    >
                      إدراج هذا السكشن
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
        {/* حقل العنوان وزر المحرر البصري */}
        <div className="flex flex-col md:flex-row md:items-center gap-4 mb-4">
          <input
            type="text"
            name="titleAr"
            value={page.titleAr}
            onChange={handleChange}
            placeholder="إضافة عنوان"
            className="flex-1 text-2xl font-bold px-4 py-3 border-b-2 border-blue-200 focus:border-blue-700 bg-transparent outline-none"
            required
          />
          <button
            type="button"
            onClick={() => navigate(`/admin/pages/${page.id}/builder`)}
            className="bg-blue-100 text-blue-900 font-bold px-6 py-2 rounded-lg border border-blue-200 hover:bg-blue-200 transition"
          >
            تحرير بواسطة المحرر البصري
          </button>
        </div>
        {/* Tabs */}
        <div className="flex border-b bg-white">
          <button
            className={`px-6 py-3 font-bold ${activeTab === 'visual' ? 'border-b-4 border-blue-700 text-blue-900' : 'text-gray-500'}`}
            onClick={() => setActiveTab('visual')}
          >
            مرئي
          </button>
          <button
            className={`px-6 py-3 font-bold ${activeTab === 'code' ? 'border-b-4 border-blue-700 text-blue-900' : 'text-gray-500'}`}
            onClick={() => setActiveTab('code')}
          >
            كود
          </button>
        </div>
        {/* Tab Content */}
        <div className="flex-1 w-full" style={{ minHeight: '350px' }}>
          {activeTab === 'visual' ? (
            <div className="h-full w-full" ref={editorContainerRef} />
          ) : (
            <div className="p-6 bg-gray-50 h-full overflow-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-blue-900">الكود البرمجي الناتج</h3>
                <button onClick={handleCopy} className="bg-blue-700 text-white px-4 py-1 rounded hover:bg-blue-800 transition">نسخ الكود</button>
              </div>
              <pre className="bg-white rounded-lg p-4 text-xs overflow-x-auto border max-h-[60vh]">
{`<style>\n${code.css}\n</style>\n${code.html}`}
              </pre>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminPageForm; 